import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Icon, cx, usePresence } from "../../../components/ui";
import {
  HARI_KERJA, HARI_SINGKAT, STATUS_LABEL, hariTeks, inisial, kalender, menitSeminggu, periode,
  rentangTeks, sesiTeks, statusTrainer, tanggalTeks
} from "./logika";
import { PIL_STATUS } from "./Linimasa";

// ─── Panel rincian satu trainer — meluncur dari kanan ────────────────────────
//
// Dipasang lewat PORTAL ke <body> (lihat catatan ModalOverlay di components/ui):
// pembungkus halaman menganimasikan transform, dan turunan `position: fixed` di bawah
// leluhur ber-transform tak lagi diukur terhadap layar.
//
// Tertutup lewat ×, klik di luar panel, atau Escape. Fokus dipindah ke tombol tutup saat
// terbuka dan dikembalikan ke baris asalnya saat tertutup.

const BULAT_DIGANTI = "bg-[#eb6834] dark:bg-[#d95926]";
const CINCIN_MENGGANTI = "border-2 border-[#0d59f2] dark:border-[#3987e5]";
const HARI_PANEL = [1, 2, 3, 4, 5, 6, 0];   // kalender & chip hari mulai Senin

const jamPerMinggu = (menit) => (menit / 60).toLocaleString("id-ID", { maximumFractionDigits: 1 });

export default function PanelTrainer({ trainer, buka, onTutup, sesi, hari, data, labelBulan, bulanBerjalan }) {
  const { terpasang, closing } = usePresence(buka, 200);
  const tombolTutup = useRef(null);
  const tutup = useRef(onTutup);
  useEffect(() => { tutup.current = onTutup; });

  // Isi terakhir disimpan supaya panel tak kosong selama meluncur keluar.
  const terakhir = useRef(trainer);
  useEffect(() => {
    if (trainer) terakhir.current = trainer;
  }, [trainer]);

  useEffect(() => {
    if (!buka) return undefined;
    const asal = document.activeElement;
    const tekan = (e) => { if (e.key === "Escape") tutup.current(); };
    document.addEventListener("keydown", tekan);
    if (tombolTutup.current) tombolTutup.current.focus();
    return () => {
      document.removeEventListener("keydown", tekan);
      if (asal && typeof asal.focus === "function" && document.contains(asal)) asal.focus();
    };
  }, [buka]);

  const t = trainer || terakhir.current;
  if (!terpasang || !t) return null;

  return createPortal(
    <div
      className={cx("fixed inset-0 z-[999] bg-black/40", closing ? "anim-fade-out" : "anim-fade")}
      onMouseDown={(e) => e.target === e.currentTarget && tutup.current()}
    >
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="ts-panel-judul"
        className={cx(
          "absolute right-0 top-0 h-full w-full max-w-[520px] overflow-y-auto bg-white shadow-2xl dark:bg-slate-900",
          closing ? "anim-panel-out" : "anim-panel-in"
        )}
      >
        <Kepala t={t} tombolTutup={tombolTutup} onTutup={() => tutup.current()} />
        <div className="space-y-6 p-5">
          {sesi && <StatusJam t={t} sesi={sesi} hari={hari} />}
          <Jadwal t={t} />
          <Bulan t={t} data={data} labelBulan={labelBulan} bulanBerjalan={bulanBerjalan} />
          <Catatan t={t} labelBulan={labelBulan} bulanBerjalan={bulanBerjalan} />
        </div>
      </aside>
    </div>,
    document.body
  );
}

function Kepala({ t, tombolTutup, onTutup }) {
  return (
    <div className="anim-rise sticky top-0 z-10 border-b border-slate-200 bg-white/95 p-5 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
      <div className="flex items-start gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-black text-white dark:bg-[#2563eb]">
          {inisial(t.nama)}
        </span>
        <div className="min-w-0 flex-1">
          <h2 id="ts-panel-judul" className="truncate text-xl font-black text-slate-900 dark:text-white">{t.nama}</h2>
          <p className="text-sm text-slate-500">
            {t.kode ? `Kode ${t.kode}` : "Belum ada kode"} · Cabang {t.cabang.length ? t.cabang.join(", ") : "—"}
          </p>
        </div>
        <button
          ref={tombolTutup}
          type="button"
          onClick={onTutup}
          aria-label="Tutup rincian"
          className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-white"
        >
          <Icon name="close" />
        </button>
      </div>
      {t.namaSama && (
        <p className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
          Ada akun lain dengan nama panggilan yang sama, jadi catatan yang hanya menyebut nama
          tidak bisa dipastikan milik siapa dan tidak dihitung. Minta admin membedakan nama
          panggilan keduanya di User Management.
        </p>
      )}
    </div>
  );
}

function StatusJam({ t, sesi, hari }) {
  const status = statusTrainer(t, sesi.sesi, hari);
  const H = hari.length ? hari : HARI_KERJA;
  const kenaHari = (k) => k.hari.some((d) => H.includes(d));
  const kenaJam = t.kelas.filter((k) => k.bentrok === null || k.bentrok.includes(sesi.sesi));
  const nama = (k) => `${k.nama} (${k.cabang}, ${sesiTeks(k.sesi)})`;
  let isi;
  if (status === "ada-kelas") {
    const kelas = kenaJam.filter((k) => k.bentrok !== null && k.hariDiisi && kenaHari(k));
    isi = `Sedang mengajar di jam ini: ${kelas.map((k) => `${nama(k)} — ${hariTeks(k.hari.filter((d) => H.includes(d)))}`).join("; ")}.`;
  } else if (status === "belum-pasti") {
    const tanpaHari = kenaJam.filter((k) => k.bentrok !== null && !k.hariDiisi);
    const takTerbaca = kenaJam.filter((k) => k.bentrok === null && (!k.hariDiisi || kenaHari(k)));
    isi = [
      tanpaHari.length && `Punya kelas di jam ini yang harinya belum diisi admin: ${tanpaHari.map(nama).join("; ")}.`,
      takTerbaca.length && `Jam kelas ${takTerbaca.map((k) => `${k.nama} (${k.cabang})`).join(", ")} tak terbaca ("${takTerbaca.map((k) => k.sesi).join('", "')}").`
    ].filter(Boolean).join(" ");
  } else {
    const hariLain = kenaJam.filter((k) => k.bentrok !== null && k.hariDiisi && !kenaHari(k) && k.hari.length);
    isi = `Tidak ada kelas di jam ini pada ${hariTeks(hari)}.`
      + (hariLain.length ? ` Kelasnya di jam ini hanya hari ${hariLain.map((k) => `${hariTeks(k.hari)} (${k.nama})`).join("; ")}.` : "");
  }
  return (
    <section className="anim-rise anim-d1 rounded-xl border border-slate-200 p-4 dark:border-slate-800">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
        Jam {rentangTeks(sesi.mulai, sesi.selesai)} · {hariTeks(hari)}
      </p>
      <p className="mt-2">
        <span className={cx("rounded-full px-2.5 py-1 text-xs font-black", PIL_STATUS[status])}>{STATUS_LABEL[status]}</span>
      </p>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{isi}</p>
    </section>
  );
}

function Jadwal({ t }) {
  const grup = new Map();
  t.kelas.forEach((k) => {
    const kunci = k.mulai == null ? `x:${k.sesi}` : `${k.mulai}-${k.selesai}`;
    if (!grup.has(kunci)) grup.set(kunci, { mulai: k.mulai, selesai: k.selesai, sesi: k.sesi, kelas: [] });
    grup.get(kunci).kelas.push(k);
  });
  const daftar = [...grup.values()].sort((a, b) => (a.mulai ?? 9999) - (b.mulai ?? 9999));
  const menit = menitSeminggu(t);

  return (
    <section className="anim-rise anim-d2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-black uppercase tracking-wide text-slate-500">Jadwal mengajar</h3>
        {menit > 0 && <span className="text-xs text-slate-400">± {jamPerMinggu(menit)} jam per minggu</span>}
      </div>
      {!daftar.length ? (
        <p className="mt-2 text-sm text-slate-500">Belum punya kelas tetap — hanya mengisi kelas trainer lain.</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {daftar.map((g) => (
            <li key={g.mulai == null ? `x:${g.sesi}` : `${g.mulai}-${g.selesai}`} className="rounded-xl border border-slate-200 p-3 dark:border-slate-800">
              <p className="text-sm font-black text-slate-900 dark:text-white">
                {g.mulai == null ? (
                  <>Jam tak terbaca · <span className="font-mono">{g.sesi || "(kosong)"}</span></>
                ) : (
                  <>
                    <span className="mr-1.5 text-[10px] uppercase tracking-wider text-slate-400">{periode(g.mulai)}</span>
                    {rentangTeks(g.mulai, g.selesai)}
                  </>
                )}
              </p>
              <ul className="mt-2 space-y-2">
                {g.kelas.map((k) => (
                  <li key={k.classId}>
                    <p className="text-sm text-slate-600 dark:text-slate-300">{k.nama} · <b>{k.cabang}</b></p>
                    {k.hariDiisi ? (
                      <div className="mt-1 flex flex-wrap gap-1" aria-label={`Hari mengajar: ${hariTeks(k.hari)}`}>
                        {HARI_PANEL.filter((d) => d !== 0 || k.hari.includes(0)).map((d) => (
                          <span
                            key={d}
                            className={cx(
                              "rounded-md px-1.5 py-0.5 text-[11px] font-bold",
                              k.hari.includes(d)
                                ? "bg-primary text-white dark:bg-[#2563eb]"
                                : "bg-slate-100 text-slate-400 line-through decoration-slate-300 dark:bg-slate-800 dark:text-slate-500"
                            )}
                          >
                            {HARI_SINGKAT[d]}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-black text-amber-700 dark:text-amber-300">
                        <Icon name="edit_calendar" className="text-xs" />
                        Hari belajarnya belum diisi admin
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Angka({ judul, nilai, catatan }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-800/40">
      {/* Dua baris judul disediakan untuk semua kotak, supaya angkanya sejajar. */}
      <p className="min-h-[1.75rem] text-[11px] font-bold uppercase leading-tight text-slate-500">{judul}</p>
      <p className="mt-1 text-lg font-black text-slate-900 dark:text-white">{nilai}</p>
      {catatan && <p className="text-[11px] leading-tight text-slate-400">{catatan}</p>}
    </div>
  );
}

function Bulan({ t, data, labelBulan, bulanBerjalan }) {
  const b = t.bulanIni;
  const diganti = new Set(b.riwayat.filter((x) => x.jenis === "diganti").map((x) => x.tanggal));
  const mengganti = new Set(b.riwayat.filter((x) => x.jenis === "mengganti").map((x) => x.tanggal));
  const iso = (d) => `${data.tahun}-${String(data.bulan).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <section className="anim-rise anim-d3">
      <h3 className="text-sm font-black uppercase tracking-wide text-slate-500">
        {labelBulan}{bulanBerjalan ? " · bulan ini" : ""}
      </h3>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <Angka judul="Total mengajar" nilai={`${b.mengajar} kali`} catatan="sesi yang benar-benar berjalan" />
        <Angka judul="Kelasnya diganti" nilai={`${b.diganti.kali} kali · ${b.diganti.hari} hari`} />
        <Angka judul="Mengganti trainer lain" nilai={`${b.mengganti.kali} kali · ${b.mengganti.hari} hari`} />
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 p-3 dark:border-slate-800">
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-black uppercase text-slate-400">
          {HARI_PANEL.map((d) => <span key={d}>{HARI_SINGKAT[d]}</span>)}
        </div>
        <div className="mt-1 grid grid-cols-7 gap-1">
          {kalender(data.tahun, data.bulan).flat().map((d, i) => {
            if (!d) return <span key={`k${i}`} />;
            const tgl = iso(d);
            const kena = [diganti.has(tgl) && "kelasnya diganti", mengganti.has(tgl) && "mengganti trainer lain"].filter(Boolean);
            const hariIni = tgl === data.hariIni;
            return (
              <span key={tgl} className="flex justify-center">
                <span
                  title={kena.length ? `${tanggalTeks(tgl)}: ${kena.join(", ")}` : undefined}
                  aria-label={`${tanggalTeks(tgl)}${hariIni ? ", hari ini" : ""}${kena.length ? `: ${kena.join(", ")}` : ""}`}
                  className={cx(
                    "flex h-9 w-9 flex-col items-center justify-center rounded-full text-xs tabular-nums",
                    hariIni && "font-black ring-2 ring-primary",
                    tgl > data.hariIni && "opacity-40"
                  )}
                >
                  {d}
                  <span className="flex h-1.5 items-center gap-0.5">
                    {diganti.has(tgl) && <span className={cx("h-1.5 w-1.5 rounded-full", BULAT_DIGANTI)} />}
                    {mengganti.has(tgl) && <span className={cx("h-1.5 w-1.5 rounded-full", CINCIN_MENGGANTI)} />}
                  </span>
                </span>
              </span>
            );
          })}
        </div>
        <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className={cx("h-2 w-2 rounded-full", BULAT_DIGANTI)} />Kelasnya diganti</span>
          <span className="inline-flex items-center gap-1.5"><span className={cx("h-2 w-2 rounded-full", CINCIN_MENGGANTI)} />Mengganti trainer lain</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full ring-2 ring-primary" />Hari ini</span>
        </p>
      </div>

      {b.perCabang.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {b.perCabang.map((c) => (
            <span key={c.cabang} className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-bold text-slate-600 dark:border-slate-700 dark:text-slate-300">
              {c.cabang} · mengajar {c.kali} kali
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

function Catatan({ t, labelBulan, bulanBerjalan }) {
  const riwayat = t.bulanIni.riwayat;
  return (
    <section className="anim-rise anim-d4">
      <h3 className="text-sm font-black uppercase tracking-wide text-slate-500">
        Catatan penggantian {bulanBerjalan ? "bulan ini" : labelBulan}
      </h3>
      {!riwayat.length ? (
        <p className="mt-2 text-sm text-slate-500">Tidak ada penggantian di bulan ini.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {riwayat.map((x, i) => (
            <li key={`${x.tanggal}|${x.sesi}|${x.jenis}|${i}`} className="flex gap-3 rounded-xl border border-slate-200 p-3 dark:border-slate-800">
              <span
                aria-hidden="true"
                className={cx("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", x.jenis === "diganti" ? BULAT_DIGANTI : CINCIN_MENGGANTI)}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  {x.jenis === "diganti" ? `Kelasnya diganti — diajar oleh ${x.lawan}` : `Mengganti trainer lain — mengajar kelas ${x.lawan}`}
                </p>
                <p className="text-xs text-slate-500">
                  {tanggalTeks(x.tanggal)} · {sesiTeks(x.sesi)} · {x.cabang}{x.tipe === "merge" ? " · kelas digabung" : ""}
                </p>
                {x.draf && (
                  <span className="mt-1 inline-flex rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-black text-amber-700 dark:text-amber-300">
                    belum dikonfirmasi
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
